// Received/read indicator.
import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';

import { Tinode } from 'tinode-sdk';

import { deliveryMarker } from '../lib/utils.js';
import SvgIcon from './svg-icon.jsx';

const messages = defineMessages({
  message_sending: {
    id: 'message_sending',
    defaultMessage: 'Sending...',
    description: 'Message being sent, in place of time stamp'
  },
  message_sending_failed: {
    id: 'message_sending_failed',
    defaultMessage: 'Failed.',
    description: 'Failed to send message, in place of time stamp'
  },
  message_status_sent: {
    id: 'message_status_sent',
    defaultMessage: 'Sent',
    description: 'Delivery status for an outgoing message that reached the server'
  },
  message_status_delivered: {
    id: 'message_status_delivered',
    defaultMessage: 'Delivered',
    description: 'Delivery status for an outgoing message received by the peer'
  },
  message_status_read: {
    id: 'message_status_read',
    defaultMessage: 'Read',
    description: 'Delivery status for an outgoing message read by the peer'
  },
  message_edited_marker: {
    id: 'message_edited_marker',
    defaultMessage: 'Edited.',
    description: 'Marker indicating that the message was edited'
  },
  tap_to_retry_title: {
    id: 'tap_to_retry_title',
    defaultMessage: 'Tap to retry',
    description: 'Title for the retry button'
  },
  tap_to_retry_label: {
    id: 'tap_to_retry_label',
    defaultMessage: 'Tap to Retry',
    description: 'Label for the retry button'
  },
  message_too_long: {
    id: 'message_too_long',
    defaultMessage: 'Message too long',
    description: 'Shown under an outgoing bubble that exceeded the character limit'
  }
});

const DELIVERY_SPRITE = {
  loading_circle: { name: 'delivery-loading', width: 16, height: 15 },
  access_time: { name: 'delivery-loading', width: 16, height: 15 },
  error: { name: 'delivery-failed', width: 15, height: 15 },
  done: { name: 'delivery-read', width: 18, height: 18 },
  done_all: { name: 'delivery-read', width: 18, height: 18 },
};

function DeliveryStatusIcon({ materialName, color }) {
  const sprite = DELIVERY_SPRITE[materialName];
  if (!sprite) {
    return null;
  }
  const className = ['delivery-status-icon', color].filter(Boolean).join(' ');
  return (
    <SvgIcon
      name={sprite.name}
      viewBox={`0 0 ${sprite.width} ${sprite.height}`}
      width={sprite.width}
      height={sprite.height}
      className={className}
      aria-hidden="true"
    />
  );
}

class ReceivedMarker extends React.PureComponent {


  handleRetry = () => {
    this.props.retrySend(this.props.pub);
  };

  deliveryStatusLabel(received, formatMessage) {
    switch (received) {
      case Tinode.MESSAGE_STATUS_SENT:
        return formatMessage(messages.message_status_sent);
      case Tinode.MESSAGE_STATUS_RECEIVED:
        return formatMessage(messages.message_status_delivered);
      case Tinode.MESSAGE_STATUS_READ:
        return formatMessage(messages.message_status_read);
      default:
        return null;
    }
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { received, timestamp, edited, deliverySyncPending, outgoing } = this.props;
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    let timeStr = '';
    if (!isNaN(date.getTime())) {
      try {
        // timeStyle needs Safari 14.1+ / iOS 14.5+.
        timeStr = date.toLocaleTimeString(this.props.intl.locale, { timeStyle: 'short' });
      } catch (_) {
        timeStr = date.toLocaleTimeString(this.props.intl.locale, {
          hour: 'numeric',
          minute: '2-digit',
        });
      }
    }
    const editedMark = edited ? formatMessage(messages.message_edited_marker) : null;

    if (!outgoing) {
      return (
        <span className="timestamp">
          {timeStr}
          {editedMark}
        </span>
      );
    }

    // `pub._sending` updates before React sees a new `received` prop; honour it so "Tap to Retry"
    // disappears as soon as a resend actually starts (e.g. after reconnect-driven flush).
    const pubSending = !!this.props.pub?._sending;

    if (deliverySyncPending) {
      return (
        <span className="timestamp">
          <DeliveryStatusIcon materialName="loading_circle" color="delivery-timestamp" />
          {'\u00a0'}
          {timeStr}
          {editedMark}
        </span>
      );
    }

    let displayText;
    // FATAL = permanent rejection (e.g. NSFW-blocked image); don't offer retry for those.
    const isFatal = !pubSending && received === Tinode.MESSAGE_STATUS_FATAL;
    const isRetryable = !pubSending && received === Tinode.MESSAGE_STATUS_FAILED;
    const isFailed = isFatal || isRetryable;
    const isTooLong = isFailed && !!this.props.pub?._tooLong;

    if (isTooLong) {
      return (
        <span className="timestamp message-too-long-status" role="status">
          <DeliveryStatusIcon materialName="error" color="danger-color" />
          <span className="message-status-failed">{formatMessage(messages.message_too_long)}</span>
        </span>
      );
    }

    if (pubSending || received <= Tinode.MESSAGE_STATUS_SENDING) {
      displayText = <span className="message-status-sending">{formatMessage(messages.message_sending)}</span>;
    } else if (isFailed) {
      displayText = <span className="message-status-failed">{formatMessage(messages.message_sending_failed)}</span>;
    } else {
      const statusLabel = this.deliveryStatusLabel(received, formatMessage);
      displayText = statusLabel ? (
        <>{statusLabel}{'\u00a0'}{timeStr}</>
      ) : timeStr;
    }

    const icon = deliveryMarker(received);
    const marker = icon ? (
      <DeliveryStatusIcon materialName={icon.name} color={icon.color} />
    ) : null;

    return (
      <span className="timestamp">
        {displayText}
        {marker && <>{'\u00a0'}{marker}</>}
        {editedMark}
        {isRetryable &&
          <span
            className="retry-message"
            onClick={this.handleRetry}
            title={formatMessage(messages.tap_to_retry_title)}
          >
            {formatMessage(messages.tap_to_retry_label)}
          </span>
        }
      </span>
    );
  }
};


export default injectIntl(ReceivedMarker);
